import { z } from 'zod';

import { looseBoolean, looseString } from '@/shared/lib/looseSchema';

/**
 * Form and change schemas.
 *
 * A "form" is an ASIC lodgement the director has to review and sign; a "change"
 * is the company alteration that produced it. Both arrive through the same
 * endpoint shape, which is why they share this module.
 */

export const formInfoSchema = z.object({
  id: z.string().default(''),
  number: z.string().default(''),
  formDate: z.string().nullish(),
  lodgedDate: z.string().nullish(),
  status: z.string().default(''),
});

export const changePartSchema = z.object({
  id: z.string().nullish(),
  asicChangeId: z.string().default(''),
  changeType: z.string().default(''),
  dateOfChange: z.string().nullish(),
});

export const changeBriefSchema = z.object({
  id: z.string().default(''),
  changeInternalId: z.string().default(''),
  parts: z.array(changePartSchema).default([]),
});

export const contactBriefSchema = z.object({
  id: z.string().default(''),
  email: z.string().default(''),
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  middleName: z.string().default(''),
  status: z.string().default(''),
});

export const formDocumentSchema = z.object({
  id: z.string().default(''),
  fileName: looseString,
  documentURL: looseString,
  status: z.string().default(''),
});

export type FormDocument = z.infer<typeof formDocumentSchema>;

/** Where a signature is stamped on the rendered PDF, and whether it has been. */
export const documentSignatureSchema = z.object({
  id: z.string().default(''),
  asicMemberId: z.string().default(''),
  documentsId: z.string().default(''),
  xPosition: z.string().default(''),
  yPosition: z.string().default(''),
  pageIndex: z.string().default(''),
  hasSigned: looseBoolean,
  signedDate: z.string().nullish(),
  viewedDate: z.string().nullish(),
});

export const formInvoiceSchema = z.object({
  id: z.string().default(''),
  amount: z.string().default(''),
  datePaid: z.string().nullish(),
  type: z.string().default(''),
  invoiceURL: looseString,
  fileName: looseString,
  status: z.string().nullish(),
});

export const sentReminderSchema = z.object({
  name: z.string().default(''),
  sentAt: z.string().nullish(),
  email: looseBoolean,
});

/**
 * `GET /director/{asicCompanyId}/form/{formId}` and its by-change sibling.
 *
 * `asicData` is the raw ASIC lodgement blob: its shape varies per form type and
 * the portal only passes it through to the renderer, so it is kept as unknown
 * rather than modelled. That is a deliberate boundary, not a gap — narrow it
 * where it is consumed, with the form type in hand.
 */
export const formFullInfoSchema = z.object({
  id: z.string().default(''),
  asicData: z.unknown().optional(),
  dueDate: z.string().nullish(),
  dateSigned: z.string().nullish(),
  asicCompany: z.object({ id: z.string().default(''), name: z.string().default('') }).nullish(),
  form: formInfoSchema.nullish(),
  change: changeBriefSchema.nullish(),
  reminders: z.array(sentReminderSchema).default([]),
  contacts: z.array(contactBriefSchema).default([]),
  documents: z.array(formDocumentSchema).default([]),
  invoices: z.array(formInvoiceSchema).default([]),
  receipt: z.string().nullish(),
  signatures: z.array(documentSignatureSchema).default([]),
  paymentStatus: z.string().nullish(),
});

export type FormFullInfo = z.infer<typeof formFullInfoSchema>;

/** `GET /director/{asicCompanyId}/forms` — the company's changes, paginated. */
export const formListSchema = z.object({
  forms: z.array(formFullInfoSchema).default([]),
  numberOfPages: z.coerce.number().default(1),
  numberOfItems: z.coerce.number().default(0),
});

export type FormList = z.infer<typeof formListSchema>;
